import { useState } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { useTranslation } from 'react-i18next';
import { Plus, X, Check, Loader2, Radio } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Popover, PopoverContent } from '@/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useQuery } from '@tanstack/react-query';
import { useAddRmaItem, useRemoveRmaItem, useUpdateRmaItem } from '../hooks/useRma';
import { InspectItemModal } from './modals/InspectItemModal';
import { RMA_REASON_LABELS } from '../types';
import type { RmaOrder, RmaItem, RmaReason, ItemCondition, ItemResolution } from '../types';
import { searchSkusApi } from '@/features/wms/api';

import { getAccountTransmitters, getAccountGateways } from '@/features/accounts/api';
import { useDebounce } from '@/hooks/useDebounce';
import type { SkuSearchResult } from '@/features/wms/types';
import type { TransmitterDetail } from '@/features/accounts/types';

// English fallbacks; the rendered label goes through t(`rma.condition.*`) / t(`rma.resolution.*`).
const ITEM_CONDITION_OPTIONS: [ItemCondition, string][] = [
  ['untested', 'Untested'],
  ['functional', 'Functional'],
  ['defective', 'Defective'],
  ['damaged', 'Damaged'],
];

const ITEM_RESOLUTION_OPTIONS: [ItemResolution, string][] = [
  ['repaired', 'Repaired'],
  ['replaced', 'Replaced'],
  ['scrapped', 'Scrapped'],
];

interface RmaItemsTableProps {
  rma: RmaOrder;
}

const canAddItems = (status: string) => status === 'draft' || status === 'label_generated';

interface InlineRowProps {
  rmaId: string;
  accountId: string;
  defaultReason: RmaReason;
  onCancel: () => void;
  onSaved: () => void;
}

type DeviceResult = { kind: 'transmitter'; item: TransmitterDetail };

function InlineAddRow({ rmaId, accountId, defaultReason, onCancel, onSaved }: InlineRowProps) {
  const { t } = useTranslation('operations');
  const addItem = useAddRmaItem();

  // Serial / device autocomplete
  const [serialSearch, setSerialSearch] = useState('');
  const [serialOpen, setSerialOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<DeviceResult | null>(null);
  const debouncedSerial = useDebounce(serialSearch, 300);

  const enabled = debouncedSerial.length >= 2 && !selectedDevice;

  const { data: transmitterResults } = useQuery({
    queryKey: ['rma-device-transmitters', accountId, debouncedSerial],
    queryFn: () =>
      getAccountTransmitters(accountId, { search: debouncedSerial, pageLength: 15 }).then((r) => r.data),
    enabled,
  });

  const { data: gatewayResults } = useQuery({
    queryKey: ['rma-device-gateways', accountId, debouncedSerial],
    queryFn: () =>
      getAccountGateways(accountId, { search: debouncedSerial, pageLength: 15 }).then((r) => r.data),
    enabled,
  });

  // Merge transmitters + gateways, deduplicate by id
  const seenIds = new Set<string>();
  const deviceResults: DeviceResult[] = [
    ...(transmitterResults ?? []),
    ...(gatewayResults ?? []),
  ]
    .filter((t) => {
      if (seenIds.has(t.id)) return false;
      seenIds.add(t.id);
      return true;
    })
    .map((t): DeviceResult => ({ kind: 'transmitter', item: t }));
  const showSerialDrop = serialOpen && deviceResults.length > 0 && !selectedDevice;

  // SKU autocomplete
  const [skuSearch, setSkuSearch] = useState('');
  const [skuOpen, setSkuOpen] = useState(false);
  const [selectedSku, setSelectedSku] = useState<SkuSearchResult | null>(null);
  const debouncedSku = useDebounce(skuSearch, 300);
  const { data: skuResults } = useQuery({
    queryKey: ['rma-sku-search', debouncedSku],
    queryFn: () => searchSkusApi(debouncedSku || undefined, 50).then((r) => r.skus),
    enabled: debouncedSku.length > 0 && !selectedSku,
  });
  const showSkuDrop = skuOpen && !!skuResults?.length && !selectedSku;

  const [quantity, setQuantity] = useState(1);
  const [reason, setReason] = useState<RmaReason>(defaultReason);
  const [condition, setCondition] = useState<ItemCondition | ''>('');
  const [resolution, setResolution] = useState<ItemResolution | ''>('');
  const [notes, setNotes] = useState('');

  const effectiveSku = selectedSku?.sku ?? null;
  const effectiveProductName = selectedSku ? (selectedSku.nameEn || selectedSku.nameFr || undefined) : undefined;
  const effectiveSerial = selectedDevice
    ? selectedDevice.item.devId
    : serialSearch.trim() || undefined;

  const canSave = effectiveSku !== null && quantity >= 1;

  const handleSelectDevice = (result: DeviceResult) => {
    setSelectedDevice(result);
    setSerialSearch(result.item.devId);
    setSerialOpen(false);
  };

  const handleSelectSku = (sku: SkuSearchResult) => {
    setSelectedSku(sku);
    setSkuSearch(sku.sku);
    setSkuOpen(false);
  };

  const handleSave = () => {
    if (!canSave) return;
    addItem.mutate(
      {
        rmaId,
        data: {
          sku: effectiveSku!,
          productName: effectiveProductName,
          quantity,
          reason,
          serialNumber: effectiveSerial,
        },
      },
      { onSuccess: onSaved },
    );
  };

  return (
    <TableRow className="bg-muted/30">
      {/* SKU */}
      <TableCell className="py-2">
        <Popover open={showSkuDrop} onOpenChange={(o) => { if (!o) setSkuOpen(false); }}>
          <PopoverPrimitive.Anchor asChild>
            <Input
              className={`h-7 text-sm font-mono w-36 ${!selectedSku ? 'border-amber-400 focus-visible:ring-amber-400' : ''}`}
              placeholder={t('rma.items.skuPlaceholder', 'Search SKU… *')}
              value={skuSearch}
              autoFocus
              onFocus={() => setSkuOpen(true)}
              onChange={(e) => {
                setSkuSearch(e.target.value);
                setSkuOpen(true);
                if (selectedSku && e.target.value !== selectedSku.sku) setSelectedSku(null);
              }}
            />
          </PopoverPrimitive.Anchor>
          <PopoverContent align="start" className="p-0 w-56" onOpenAutoFocus={(e) => e.preventDefault()}>
            <div className="max-h-48 overflow-y-auto">
              {skuResults?.map((sku) => (
                <button
                  key={sku.id}
                  type="button"
                  className="w-full px-3 py-2 text-left hover:bg-accent text-sm"
                  onMouseDown={(e) => { e.preventDefault(); handleSelectSku(sku); }}
                >
                  <div className="font-mono font-medium">{sku.sku}</div>
                  <div className="text-muted-foreground text-xs truncate">{sku.nameEn || sku.nameFr}</div>
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
        {selectedSku && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-[144px]">
            {selectedSku.nameEn || selectedSku.nameFr}
          </p>
        )}
      </TableCell>

      {/* Serial Number — searches account transmitters (devId) and sensors (serial) */}
      <TableCell className="py-2">
        <Popover open={showSerialDrop} onOpenChange={(o) => { if (!o) setSerialOpen(false); }}>
          <PopoverPrimitive.Anchor asChild>
            <Input
              className="h-7 text-sm font-mono w-44"
              placeholder={t('rma.items.serialPlaceholder', 'S/N ou DevID…')}
              value={serialSearch}
              onFocus={() => setSerialOpen(true)}
              onChange={(e) => {
                setSerialSearch(e.target.value);
                setSerialOpen(true);
                if (selectedDevice) setSelectedDevice(null);
              }}
            />
          </PopoverPrimitive.Anchor>
          <PopoverContent align="start" className="p-0 w-80" onOpenAutoFocus={(e) => e.preventDefault()}>
            <div className="max-h-56 overflow-y-auto">
              {deviceResults.map((result) => {
                const dev = result.item;
                return (
                  <button
                    key={`tx-${dev.id}`}
                    type="button"
                    className="w-full px-3 py-2 text-left hover:bg-accent text-sm"
                    onMouseDown={(e) => { e.preventDefault(); handleSelectDevice(result); }}
                  >
                    <div className="flex items-center gap-1.5">
                      <Radio className="h-3 w-3 text-muted-foreground shrink-0" />
                      <span className="font-mono font-medium text-xs">{dev.devId}</span>
                    </div>
                    <div className="text-muted-foreground text-xs mt-0.5 ml-5 truncate">
                      {dev.name || dev.deviceType || t('rma.items.transmitter', 'Transmitter')}
                      {dev.siteName ? ` · ${dev.siteName}` : ''}
                    </div>
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
        {selectedDevice && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-[176px]">
            {selectedDevice.item.name || selectedDevice.item.deviceType || ''}
          </p>
        )}
      </TableCell>

      {/* Product Name (auto from SKU) */}
      <TableCell className="py-2 text-sm text-muted-foreground">
        {effectiveProductName || '—'}
      </TableCell>

      {/* Qty */}
      <TableCell className="py-2 text-right">
        <Input
          type="number"
          min={1}
          className="h-7 text-sm w-16 text-right"
          value={quantity}
          onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
        />
      </TableCell>

      {/* Reason */}
      <TableCell className="py-2">
        <Select value={reason} onValueChange={(v) => setReason(v as RmaReason)}>
          <SelectTrigger className="h-7 text-sm w-36">
            <SelectValue placeholder={t('rma.items.reasonPlaceholder', 'Reason…')} />
          </SelectTrigger>
          <SelectContent>
            {(Object.entries(RMA_REASON_LABELS) as [RmaReason, string][]).map(([value, label]) => (
              <SelectItem key={value} value={value}>{t(`rma.reason.${value}`, label)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>

      {/* Condition */}
      <TableCell className="py-2">
        <Select value={condition} onValueChange={(v) => setCondition(v as ItemCondition | '')}>
          <SelectTrigger className="h-7 text-sm w-28">
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent>
            {ITEM_CONDITION_OPTIONS.map(([value, label]) => (
              <SelectItem key={value} value={value}>{t(`rma.condition.${value}`, label)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>

      {/* Resolution */}
      <TableCell className="py-2">
        <Select value={resolution} onValueChange={(v) => setResolution(v as ItemResolution | '')}>
          <SelectTrigger className="h-7 text-sm w-28">
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent>
            {ITEM_RESOLUTION_OPTIONS.map(([value, label]) => (
              <SelectItem key={value} value={value}>{t(`rma.resolution.${value}`, label)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>

      {/* Notes */}
      <TableCell className="py-2">
        <Input
          className="h-7 text-sm w-32"
          placeholder={t('rma.items.notesPlaceholder', 'Notes…')}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </TableCell>

      {/* Actions */}
      <TableCell className="py-2">
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-success hover:text-success"
            onClick={handleSave}
            disabled={!canSave || addItem.isPending}
            title={t('rma.items.saveItem', 'Save item')}
          >
            {addItem.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground"
            onClick={onCancel}
            title={t('rma.items.cancel', 'Cancel')}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function EditableItemRow({ item, rmaId }: { item: RmaItem; rmaId: string }) {
  const { t } = useTranslation('operations');
  const updateItem = useUpdateRmaItem();
  const removeItem = useRemoveRmaItem();

  const [reason, setReason] = useState<RmaReason>(item.reason ?? 'other');
  const [condition, setCondition] = useState<ItemCondition | ''>(item.conditionOnReceipt ?? '');
  const [resolution, setResolution] = useState<ItemResolution | ''>(item.itemResolution ?? '');
  const [notes, setNotes] = useState(item.itemNotes ?? '');
  const [notesDirty, setNotesDirty] = useState(false);

  const save = (patch: Parameters<typeof updateItem.mutate>[0]['data']) =>
    updateItem.mutate({ rmaId, itemId: item.id, data: patch });

  return (
    <TableRow key={item.id}>
      <TableCell className="font-mono text-sm">{item.sku}</TableCell>
      <TableCell className="font-mono text-sm">
        {item.serialNumber || <span className="text-muted-foreground">--</span>}
      </TableCell>
      <TableCell className="text-sm">
        {item.productName || <span className="text-muted-foreground">--</span>}
      </TableCell>
      <TableCell className="text-sm text-right">{item.quantity ?? 1}</TableCell>

      {/* Reason */}
      <TableCell className="py-1.5">
        <Select value={reason} onValueChange={(v) => { setReason(v as RmaReason); save({ reason: v as RmaReason }); }}>
          <SelectTrigger className="h-7 text-sm w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.entries(RMA_REASON_LABELS) as [RmaReason, string][]).map(([value, label]) => (
              <SelectItem key={value} value={value}>{t(`rma.reason.${value}`, label)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>

      {/* Condition */}
      <TableCell className="py-1.5">
        <Select value={condition} onValueChange={(v) => { setCondition(v as ItemCondition); save({ conditionOnReceipt: v as ItemCondition }); }}>
          <SelectTrigger className="h-7 text-sm w-28">
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent>
            {ITEM_CONDITION_OPTIONS.map(([value, label]) => (
              <SelectItem key={value} value={value}>{t(`rma.condition.${value}`, label)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>

      {/* Resolution */}
      <TableCell className="py-1.5">
        <Select value={resolution} onValueChange={(v) => { setResolution(v as ItemResolution); save({ itemResolution: v as ItemResolution }); }}>
          <SelectTrigger className="h-7 text-sm w-28">
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent>
            {ITEM_RESOLUTION_OPTIONS.map(([value, label]) => (
              <SelectItem key={value} value={value}>{t(`rma.resolution.${value}`, label)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>

      {/* Notes */}
      <TableCell className="py-1.5">
        <Input
          className="h-7 text-sm w-36"
          placeholder="—"
          value={notes}
          onChange={(e) => { setNotes(e.target.value); setNotesDirty(true); }}
          onBlur={() => { if (notesDirty) { save({ itemNotes: notes.trim() || undefined }); setNotesDirty(false); } }}
        />
      </TableCell>

      {/* Actions */}
      <TableCell>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-destructive hover:text-destructive"
          onClick={() => removeItem.mutate({ rmaId, itemId: item.id })}
          disabled={removeItem.isPending}
          title={t('rma.items.removeItem', 'Remove item')}
        >
          <X className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}

export function RmaItemsTable({ rma }: RmaItemsTableProps) {
  const { t } = useTranslation('operations');
  const [showNewRow, setShowNewRow] = useState(false);
  const [inspectItem, setInspectItem] = useState<RmaItem | null>(null);

  const showTable = rma.items.length > 0 || showNewRow;

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-base font-medium">
            {t('rma.items.title', 'Items ({{count}})', { count: rma.items.length })}
          </CardTitle>
          {canAddItems(rma.status) && !showNewRow && (
            <Button variant="outline" size="sm" onClick={() => setShowNewRow(true)}>
              <Plus className="h-4 w-4 mr-1" />
              {t('rma.items.add', 'Add Item')}
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {!showTable ? (
            <div className="px-6 pb-6 text-center text-sm text-muted-foreground">
              {t('rma.items.empty', 'No items added yet.')}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>{t('rma.items.col.sku', 'SKU')}</TableHead>
                  <TableHead>{t('rma.items.col.serial', 'Serial Number')}</TableHead>
                  <TableHead>{t('rma.items.col.product', 'Product Name')}</TableHead>
                  <TableHead className="text-right">{t('rma.items.col.qty', 'Qty')}</TableHead>
                  <TableHead>{t('rma.items.col.reason', 'Reason')}</TableHead>
                  <TableHead>{t('rma.items.col.condition', 'Condition')}</TableHead>
                  <TableHead>{t('rma.items.col.resolution', 'Resolution')}</TableHead>
                  <TableHead>{t('rma.items.col.notes', 'Notes')}</TableHead>
                  <TableHead className="w-[100px]">{t('rma.items.col.actions', 'Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rma.items.map((item) => (
                  <EditableItemRow key={item.id} item={item} rmaId={rma.id} />
                ))}

                {showNewRow && (
                  <InlineAddRow
                    rmaId={rma.id}
                    accountId={rma.accountId}
                    defaultReason={rma.reason}
                    onCancel={() => setShowNewRow(false)}
                    onSaved={() => setShowNewRow(false)}
                  />
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {inspectItem && (
        <InspectItemModal
          open={!!inspectItem}
          onOpenChange={(open) => { if (!open) setInspectItem(null); }}
          rmaId={rma.id}
          item={inspectItem}
        />
      )}
    </>
  );
}
